import React, { FC, useMemo, useState } from "react"
import { useSwipeable } from "react-swipeable"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"

import { BespokeComponentFragmentFragment, MessagesFeaturesQueryQuery } from "@graphqlTypes"

import LpSection from "@components/lp/LpSection/LpSection"

import "./MessagesFeatures.scss"

type MessagesFeaturesJsonPayload = {
  title: string
  description: string
}

const MESSAGES_FEATURES_ITEMS = [
  {
    slug: "standard",
    title: "Standard",
  },
  {
    slug: "nps",
    title: "NPS®",
  },
  {
    slug: "microsurvey",
    title: "Microsurvey",
  },
  {
    slug: "modal",
    title: "Modal",
  },
  {
    slug: "banner",
    title: "Banner",
  },
]

const MessagesFeatures: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload?: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const [activeStyleTile, setActiveStyleTile] = useState<number>(0)

    const queryData = useStaticQuery<MessagesFeaturesQueryQuery>(graphql`
      query MessagesFeaturesQuery {
        selectedIcon: contentfulAsset(contentful_id: {eq: "3dP0GKjVY5lkVe5DWfPsvs"}) {
          imgixImage {
            url
          }
        }
        microsurveyIcon: contentfulAsset(contentful_id: {eq: "eOggUYqjdRAKGgpUeBMjM"}) {
          imgixImage {
            url
          }
        }
        bannerIcon: contentfulAsset(contentful_id: {eq: "45VYj5E8R35O9PJWVwpGHV"}) {
          imgixImage {
            url
          }
        }
        modalIcon: contentfulAsset(contentful_id: {eq: "6mW7gGDAkwhwMBH7YIUAu3"}) {
          imgixImage {
            url
          }
        }
        npsIcon: contentfulAsset(contentful_id: {eq: "3CWuiATTJsKC7PMyRd88EQ"}) {
          imgixImage {
            url
          }
        }
        standardIcon: contentfulAsset(contentful_id: {eq: "5bWyaWaOn9FdmXcPhNa8xN"}) {
          imgixImage {
            url
          }
        }
        npsImage: contentfulAsset(contentful_id: {eq: "5QYWJtVG8hdyrbzjqKfClh"}) {
          imgixImage {
            gatsbyImageData(width: 616)
          }
        }
        microsurveyImage: contentfulAsset(contentful_id: {eq: "16Lv5vM6No9UwsDtTJY087"}) {
          imgixImage {
            gatsbyImageData(width: 616)
          }
        }
        bannerImage: contentfulAsset(contentful_id: {eq: "7bl3uNmwafnRhWbJTs7o7F"}) {
          imgixImage {
            gatsbyImageData(width: 616)
          }
        }
        standardImage: contentfulAsset(contentful_id: {eq: "2CEgdbwFvjx7YxX9kp5JNW"}) {
          imgixImage {
            gatsbyImageData(width: 616)
          }
        }
        modalImage: contentfulAsset(contentful_id: {eq: "3zZVZW62RAnpgzTTIszXUl"}) {
          imgixImage {
            gatsbyImageData(width: 616)
          }
        }
      }
    `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as MessagesFeaturesJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: HomeHero (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    const imagesSwipeHandlers = useSwipeable({
      onSwipedLeft: () => setActiveStyleTile(_i => _i === MESSAGES_FEATURES_ITEMS.length - 1 ? _i : _i + 1),
      onSwipedRight: () => setActiveStyleTile(_i => _i === 0 ? _i : _i - 1),
    })

    const tilesSwipeHandlers = useSwipeable({
      onSwipedLeft: () => setActiveStyleTile(_i => _i === MESSAGES_FEATURES_ITEMS.length - 1 ? _i : _i + 1),
      onSwipedRight: () => setActiveStyleTile(_i => _i === 0 ? _i : _i - 1),
    })

    return (
      <LpSection
        className="MessagesFeatures"
        uniqueId={contentful_id}
      >
        <div className="container">
          <div className="grid">
            <div className="MessagesFeatures--Title">
              <h2>{json.title}</h2>
              <p>{json.description}</p>
            </div>
            <div
              className="MessagesFeatures--StyleTiles"
              {...tilesSwipeHandlers}
            >
              <div className="tiles-frame">
                <div
                  className="tiles-grid"
                  style={{
                    translate: `calc(${activeStyleTile} * var(--MessagesFeatures--StyleTiles--offset) * -1)`
                  }}
                >
                  {MESSAGES_FEATURES_ITEMS.map((item, i) => (
                    <button
                      aria-label={item.title}
                      className={`MessagesFeatures--StyleTile${activeStyleTile === i ? " is-active" : ""}`}
                      key={item.slug}
                      onClick={() => setActiveStyleTile(i)}
                    >
                      {queryData.selectedIcon?.imgixImage?.url && (
                        <img
                          className="selected-icon"
                          src={queryData.selectedIcon.imgixImage.url}
                        />
                      )}
                      {queryData[`${item.slug}Icon`]?.imgixImage?.url && (
                        <img
                          className="item-icon"
                          src={queryData[`${item.slug}Icon`].imgixImage.url}
                        />
                      )}
                      <span>{item.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="MessagesFeatures--Images"
              {...imagesSwipeHandlers}
            >
              <div className="images-frame">
                <div
                  className="images-grid"
                  style={{
                    translate: `calc(${activeStyleTile} * var(--MessagesFeatures--Images--offset) * -1)`
                  }}
                >
                  {MESSAGES_FEATURES_ITEMS.map((item, i) => (
                    <>
                      {queryData[`${item.slug}Image`]?.imgixImage?.gatsbyImageData && (
                        <GatsbyImage
                          alt={item.title}
                          className={`item-image${activeStyleTile === i ? " is-active" : ""}`}
                          image={queryData[`${item.slug}Image`].imgixImage.gatsbyImageData}
                        />
                      )}
                    </>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </LpSection>
    )
  }

export default MessagesFeatures
